import React, { useEffect, useMemo, useState } from 'react';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { capitalize, debounce } from 'lodash';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import apis from '@src/apis';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import {
  DEBOUNCE_DELAY,
  MAX_LENGTH_INPUT_PRONUNCIATION,
  MAX_LENGTH_INPUT_WORD,
  PLACEHOLDER_INPUT_HOW_TO_SPEAK,
  PLACEHOLDER_INPUT_NEW_WORD,
} from '@src/constants/dictionary';
import actions from '@src/redux/actions';

import Tip from './Tip';
import { checkValidatePronunciation, validateValueInputWord } from './utils';
import { StyledButton, StyledInputCount } from './index.style';

const InputCount = ({
  onBlur,
  onChange,
  errorMsg,
  value,
  name,
  placeholder,
  title,
  maxChar,
}) => (
  <StyledInputCount isError={!!errorMsg}>
    <div className="title_input">{title}</div>
    <div className="row_input">
      <div className="input_count">
        <input
          onBlur={onBlur}
          onChange={onChange}
          value={value}
          name={name}
          spellcheck="false"
          placeholder={placeholder}
        />
        <div className="count_text">
          {value?.length}/{maxChar}
        </div>
      </div>
    </div>
    <div className="msg_error">{errorMsg}</div>
  </StyledInputCount>
);

const FormAddDictionary = ({
  onTextChangeWhenAddWord,
  wordsData,
  fetchDictionary,
  currentItem,
}) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const { getFeatureValue } = useFeatureFlags();
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const { playAudioButtonId } = useSelector((state) => state.dictionary);
  const [loadingAddDictionary, setLoadingAddDictionary] = useState(false);
  const [dataForm, setDataForm] = useState({
    inputWord: '',
    inputPronunciation: '',
  });

  const [errorMsg, setErrorMsg] = useState({
    inputWord: '',
    inputPronunciation: '',
  });
  const [openTips, setOpenTips] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const dispatch = useDispatch();

  const refClickOutSide = useClickOutside(() => {
    setOpenTips(false);
  });
  const useSuggestDictionary = getFeatureValue(
    FEATURE_KEYS.SUGGEST_DICTIONARY,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );
  // Mock API
  const fetchSuggestions = async (word) => {
    setLoadingSuggestions(true);
    // fake loading
    await new Promise((resolve) => setTimeout(resolve, 500));
    const fakeData = word
      ? [`${word}-a`, `${word}-b`, `${word}-c`, `${word}-d`]
      : [];
    setLoadingSuggestions(false);
    return fakeData;
  };

  const debouncedFetchSuggestions = debounce(async (word) => {
    const result = await fetchSuggestions(word);
    setSuggestions(result);
  }, DEBOUNCE_DELAY);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDataForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'inputWord') {
      debouncedFetchSuggestions(value);
    }
    if (playAudioButtonId === name) {
      dispatch(actions.dictionary.resetPreviewAudio());
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setDataForm((prev) => ({
      ...prev,
      inputPronunciation: suggestion,
    }));
  };

  const checkValidateForm = (value, key) => {
    if (key === 'inputPronunciation') {
      const [invalid, empty, overMaxLength] = checkValidatePronunciation(
        value?.trim(),
      );
      if (empty) {
        setErrorMsg((prev) => ({
          ...prev,
          [key]: t('invalidEmptyPronunciation'),
        }));
        return;
      }
      if (invalid) {
        setErrorMsg((prev) => ({
          ...prev,
          [key]: t('invalidPronunciationOfNewDictionary'),
        }));
        return;
      }
      if (overMaxLength) {
        setErrorMsg((prev) => ({
          ...prev,
          [key]: t('overMaxLengthPronunciation'),
        }));
        return;
      }
      setErrorMsg((prev) => ({
        ...prev,
        [key]: '',
      }));
      return;
    }
    const [trim, empty, duplicate, invalid] = validateValueInputWord(
      wordsData,
      value,
    );
    if (empty) {
      setErrorMsg((prev) => ({
        ...prev,
        [key]: t('emptyTextDictionary'),
      }));
      return;
    }
    if (duplicate) {
      setErrorMsg((prev) => ({
        ...prev,
        [key]: t('duplicateTextDictionary'),
      }));
      return;
    }
    if (trim) {
      setErrorMsg((prev) => ({
        ...prev,
        [key]: t('maxCharTextDictionary'),
      }));
      return;
    }
    if (invalid) {
      setErrorMsg((prev) => ({
        ...prev,
        [key]: t('invalidWordOfNewDictionary'),
      }));
      return;
    }

    setErrorMsg((prev) => ({
      ...prev,
      [key]: '',
    }));
  };

  const [invalidInput, invalidInputWord, invalidInputPro] = useMemo(() => {
    const [trim, emptyWord, duplicate, invalidWord] = validateValueInputWord(
      wordsData,
      dataForm.inputWord,
    );
    const [invalid, empty, overMaxLength] = checkValidatePronunciation(
      dataForm.inputPronunciation?.trim(),
    );
    return [
      [
        invalid,
        empty,
        overMaxLength,
        trim,
        emptyWord,
        duplicate,
        invalidWord,
      ]?.some(Boolean),
      [trim, emptyWord, duplicate, invalidWord]?.some(Boolean),
      [invalid, empty, overMaxLength]?.some(Boolean),
    ];
  }, [dataForm, wordsData]);

  const onCreateDictionary = async () => {
    try {
      setErrorMsg({
        inputWord: '',
        inputPronunciation: '',
      });
      setLoadingAddDictionary(true);
      const data = await apis.voices.createDictionary([
        {
          word: dataForm.inputWord?.trim(),
          pronunciation: dataForm.inputPronunciation?.trim(),
        },
        ...wordsData,
      ]);
      if (data?.status) {
        setDataForm({
          inputWord: '',
          inputPronunciation: '',
        });
        const words = await fetchDictionary(true);
        onTextChangeWhenAddWord(words[0]);
      }
    } finally {
      setLoadingAddDictionary(false);
    }
  };
  const disabledAddWord = loadingAddDictionary || invalidInput;
  const handleMouseDown = () => {
    setIsButtonPressed(true);
  };
  const handleMouseUp = () => {
    setIsButtonPressed(false);
  };

  useEffect(() => {
    if (currentItem) {
      setDataForm((prev) => ({
        ...prev,
        inputWord: currentItem.word,
        inputPronunciation: currentItem.pronunciation,
      }));
      checkValidateForm(currentItem.word, 'inputWord');
      checkValidateForm(currentItem.pronunciation, 'inputPronunciation');
    }
  }, [currentItem]);

  return (
    <div className="forms">
      <div className="noti_header">
        <div style={{ display: 'flex' }}>
          <TipsAndUpdatesIcon className="tipicon" />
        </div>

        <div ref={refClickOutSide}>
          <span className="title_tip">
            {t('titleNotiNewDictionary')}{' '}
            {wordsData?.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                onClick={() => setOpenTips(true)}
                className="link_view_tip"
              >
                {t('seeGreatTips')}.
              </span>
            )}
          </span>
          {openTips && <Tip onClose={() => setOpenTips(false)} absolute />}
        </div>
      </div>
      <div className="forms_input">
        <InputCount
          value={dataForm.inputWord}
          name="inputWord"
          onChange={handleInputChange}
          invalidInput={invalidInputWord}
          maxChar={MAX_LENGTH_INPUT_WORD}
          errorMsg={errorMsg?.inputWord}
          onBlur={(e) => {
            if (isButtonPressed) return;
            checkValidateForm(e.target.value, 'inputWord');
          }}
          placeholder={PLACEHOLDER_INPUT_NEW_WORD}
          title={t('AbbreviationsforeignWords')}
        />
      </div>
      <div className="forms_input">
        <InputCount
          onChange={handleInputChange}
          invalidInput={invalidInputPro}
          onBlur={(e) => {
            if (isButtonPressed) return;
            checkValidateForm(e.target.value, 'inputPronunciation');
          }}
          maxChar={MAX_LENGTH_INPUT_PRONUNCIATION}
          errorMsg={errorMsg.inputPronunciation}
          value={dataForm.inputPronunciation}
          name="inputPronunciation"
          placeholder={PLACEHOLDER_INPUT_HOW_TO_SPEAK}
          title={capitalize(t('pronunciation'))}
        />
      </div>
      {useSuggestDictionary && suggestions && suggestions.length > 0 && (
        <div className="suggestions-container">
          <Typography className="suggestions-title">
            {t('readingSuggest')}:
          </Typography>
          {loadingSuggestions ? (
            <CircularProgress size="24px" />
          ) : (
            <Box className="list-reading-suggest-btn">
              {suggestions.map((suggestion, index) => (
                <Button
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  variant="outlined"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="reading-suggest-btn"
                  endIcon={<VolumeUpRoundedIcon className="icon" />}
                >
                  {suggestion}
                </Button>
              ))}
            </Box>
          )}
        </div>
      )}
      <StyledButton disabledBtn={disabledAddWord} className="btn-box">
        <Button
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onClick={onCreateDictionary}
          color="primary"
          variant="contained"
          disabled={disabledAddWord}
          className="add-word-button"
          data-id="create-new-pronunciation"
        >
          {loadingAddDictionary ? (
            <CircularProgress value={75} className="loading-load" size="24px" />
          ) : (
            capitalize(t('addword'))
          )}
        </Button>
      </StyledButton>
    </div>
  );
};

export default FormAddDictionary;
